import { autocompletion, CompletionContext } from '@codemirror/autocomplete'
import { json, jsonParseLinter } from '@codemirror/lang-json'
import { linter } from '@codemirror/lint'
import { CodeOutlined, FormatListBulletedOutlined, InfoRounded, MoreHorizRounded, SearchRounded } from '@mui/icons-material'
import { Box, Button, ButtonGroup, IconButton, InputAdornment, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import CodeMirror from '@uiw/react-codemirror'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { IFlow, INode, INodeVariableMode, JsonSchema } from '../../../../../definitions/Nodes'
import { IAcceptedData } from '../../../../../definitions/NodesDescription'
import NodeManager from '../../../../../managers/NodeManager'
import { useAppSelector } from '../../../../../store/Hooks'
import RenderTableSchema from '../../../nodes/RenderTableSchema'
import CustomDataList from './CustomDataList'

interface P {
    acceptedData: IAcceptedData<'var_variable'>
    selectedNode: INode<'var_variable'>
    flow: IFlow
    setSelected: (node: INode<'var_variable'>) => void
    disable: boolean
}

const NodeVariableEditor = ({ acceptedData, selectedNode, flow, setSelected, disable }: P) => {
    const mode = useAppSelector(state => state.app.mode)
    const [customMode, setCustomMode] = useState<'normal' | 'advanced'>('normal')
    const [hasLintError, setHasLintError] = useState(false)
    const [search, setSearch] = useState('')
    const numberInputs = selectedNode.inputs.length
    const selectedInputNodes = flow.nodes.flatMap(node => selectedNode.inputs.filter(input => input.uuid === node.uuid))
    const inputNodes = flow.nodes.filter(node => selectedNode.inputs.some(input => input.uuid === node.uuid))
    const shapes = NodeManager.getInputShapes(selectedNode, flow.nodes)
    const globalsSuggestions = useMemo(() => {
        const globalsShape = flow?.globals ? Object.keys(flow.globals) : []
        return globalsShape.length > 0
            ? globalsShape.map(x => ({
                label: `globals.${x}`,
                type: 'fieldInput',
                info: '-> Field Input',
                apply: `@{globals.${x}}`
            }))
            : []
    }, [flow.globals])

    const myCompletions = useCallback((context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return {
            from: word.from,
            options: globalsSuggestions
        }
    }, [])

    const filteredIndices = useMemo(() => {
        if (!search) return null
        const entries = Object.entries(selectedNode.data?.item?.customData ?? {})
        return entries
            .map(([k], idx) => ({ k, idx }))
            .filter(({ k }) => k.toLowerCase().includes(search.toLowerCase()))
            .map(({ idx }) => idx)
    }, [selectedNode.data?.item?.customData, search])

    useEffect(() => {
        if (!selectedNode.data?.item?.copy || !selectedNode.data?.item?.customData) {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    item: {
                        ...selectedNode.data.item,
                        mode: 'custom',
                        copy: { key: '', uuid: '' },
                        customData: { key_1: '' }
                    }
                }
            })
        }
    }, [])

    useEffect(() => {
        if (selectedNode.data?.item?.customData && Array.isArray(selectedNode.data.item.customData)) {
            setCustomMode('advanced')
        }
    }, [selectedNode])

    const renderInfoField = (title: string | ReactNode) => (
        <Tooltip arrow title={title} disableHoverListener={disable}>
            <InfoRounded fontSize='inherit' sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
        </Tooltip>
    )

    const getMergeData = () => {
        const outputKeyMap: JsonSchema = { type: 'object', properties: {} }
        selectedInputNodes.forEach(node => {
            const inputNode = inputNodes.find(n => n.uuid === node.uuid)
            const inputShape = shapes.find(s => s.nodeName === inputNode.name)
            const nodeName = inputNode.name
            if (!outputKeyMap.properties[nodeName]) {
                outputKeyMap.properties[nodeName] = {}
            }
            outputKeyMap.properties[nodeName] = inputShape?.shape
        })
        return outputKeyMap
    }

    const handleChange = (data: INodeVariableMode) => {
        setSelected({
            ...selectedNode, data: {
                ...selectedNode.data, [acceptedData.parameter]: data
            }
        })
    }

    const renderCustom = () => {
        return (
            <Stack spacing={3}>
                <Stack flexDirection='row' alignItems='center' justifyContent='space-between'>
                    <Stack flexDirection='row' gap={1.5} alignItems='center'>
                        {renderInfoField(customMode === 'normal' ? 'normal' : 'advanced')}
                        <Typography variant='subtitle2'>{customMode === 'normal' ? 'List mode' : 'Advanced mode'}:</Typography>
                    </Stack>
                    <Stack flexDirection='row' gap={1.5} alignItems='center' justifyContent='flex-end'>
                        {customMode === 'normal' && (
                            <Box width='50%'>
                                <TextField
                                    size='small'
                                    placeholder='Search...'
                                    value={search}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <SearchRounded fontSize='small' />
                                            </InputAdornment>
                                        )
                                    }}
                                    onChange={e => setSearch(e.target.value)}
                                    disabled={disable}
                                />
                            </Box>
                        )}
                        <Box>
                            <ToggleButtonGroup
                                size='small'
                                color='primary'
                                value={customMode}
                                exclusive
                                onChange={(e, v) => {
                                    if (v !== null)
                                        setCustomMode(v)
                                }}>
                                <Tooltip title='normal' arrow>
                                    <ToggleButton size='small' value='normal' disabled={(selectedNode.data?.item?.customData && Array.isArray(selectedNode.data.item.customData)) || hasLintError}>
                                        <FormatListBulletedOutlined fontSize='small' />
                                    </ToggleButton>
                                </Tooltip>
                                <Tooltip title='advanced' arrow>
                                    <ToggleButton size='small' value='advanced'>
                                        <CodeOutlined fontSize='small' />
                                    </ToggleButton>
                                </Tooltip>
                            </ToggleButtonGroup>
                        </Box>
                    </Stack>
                </Stack>
                <Stack spacing={1}>
                    {customMode === 'normal' && (
                        <Stack flexDirection='row' gap={1} alignItems='center'>
                            <Box flex={1}>
                                <Typography>Key</Typography>
                            </Box>
                            <Box flex={1}>
                                <Typography>Value</Typography>
                            </Box>
                            <Box visibility='hidden'>
                                <IconButton size='small'>
                                    <MoreHorizRounded fontSize='small' />
                                </IconButton>
                            </Box>
                        </Stack>
                    )}
                    {customMode === 'normal'
                        ? (
                                <CustomDataList
                                    data={selectedNode.data?.item?.customData}
                                    setSelected={setSelected}
                                    visibleIndices={filteredIndices}
                                    myCompletions={myCompletions}
                                    disable={disable}
                                    setSearch={setSearch}
                                />
                            )
                        : (
                                <CodeMirror
                                    readOnly={disable}
                                    theme={mode === 'dark' ? 'dark' : 'light'}
                                    value={selectedNode.data?.item?.customData ? JSON.stringify(selectedNode.data.item.customData, null, 2) : ''}
                                    width='100%'
                                    maxHeight='55vh'
                                    minHeight='30vh'
                                    extensions={[json(), linter(jsonParseLinter()), autocompletion({ override: [myCompletions] })]}
                                    onChange={(v: string) => {
                                        try {
                                            const parsedValue = JSON.parse(v)
                                            handleChange({ ...selectedNode.data.item, customData: parsedValue })
                                            setHasLintError(false)
                                        } catch (error) {
                                            console.error(error)
                                            setHasLintError(true)
                                        }
                                    }}
                                />
                            )}
                </Stack>
            </Stack>
        )
    }

    const renderCopy = () => {
        const inputNode = selectedInputNodes.filter(node => node.uuid === selectedNode.data?.item?.copy.uuid)
        const inputShape = shapes.find(s => inputNode.find(iN => iN.uuid === s.nodeUuid && iN.outputKey === selectedNode.data?.item?.copy?.key))?.type === 'exe_subFlow'
            // @ts-ignore
            ? shapes.find(s => inputNode.find(iN => iN.uuid === s.nodeUuid && iN.outputKey === selectedNode.data?.item?.copy?.key))?.shape.value
            : shapes.find(s => inputNode.find(iN => iN.uuid === s.nodeUuid && iN.outputKey === selectedNode.data?.item?.copy?.key))?.shape
        return (
            <Stack spacing={2}>
                <TextField
                    id='linkedNodeInput'
                    select
                    size='small'
                    label='Linked Node'
                    disabled={disable}
                    value={selectedNode.data?.item?.copy.uuid}
                    onChange={e => {
                        e.preventDefault()
                        const uuid = inputNodes.find(input => input.uuid === e.target.value).uuid
                        const key = selectedInputNodes.find(node => node.uuid === e.target.value).outputKey
                        handleChange({ ...selectedNode.data?.item, copy: { ...selectedNode.data?.item?.copy, uuid: uuid, key: key } })
                    }}
                    InputLabelProps={{ shrink: true }}>
                    {inputNodes.map(node => (
                        <MenuItem key={node.uuid} value={node.uuid}>
                            {node.name}
                        </MenuItem>
                    ))}
                </TextField>
                {inputNode.length > 1
                    ? (
                            <>
                                <TextField
                                    id='outputNodeInput'
                                    select
                                    size='small'
                                    label='Output Node'
                                    disabled={disable}
                                    value={selectedNode.data?.item?.copy.key}
                                    onChange={e => {
                                        e.preventDefault()
                                        const key = inputNode.find(input => input.outputKey === e.target.value).outputKey
                                        handleChange({ ...selectedNode.data?.item, copy: { ...selectedNode.data?.item?.copy, key: key } })
                                    }}
                                    InputLabelProps={{ shrink: true }}>
                                    {inputNode.map(node => (
                                        selectedNode.inputs.find(i => i.outputKey === node.outputKey) && (
                                            <MenuItem key={node.uuid + node.outputKey} value={node.outputKey}>
                                                {node.outputKey}
                                            </MenuItem>
                                        )
                                    ))}
                                </TextField>
                                <RenderTableSchema data={inputShape} maxHeight={400} />
                            </>
                        )
                    : (<RenderTableSchema data={inputShape} maxHeight={400} />)}
            </Stack>
        )
    }

    const renderMerge = () => (
        <Stack>
            <RenderTableSchema data={getMergeData()} maxHeight={480} />
        </Stack>
    )

    return (
        <Stack height='100%' flex={1} spacing={3} justifyContent='center'>
            <Stack alignItems='center'>
                <ButtonGroup>
                    <Button
                        variant={selectedNode.data?.item?.mode === 'custom' ? 'contained' : undefined}
                        onClick={() => handleChange({ ...selectedNode.data.item, mode: 'custom' })}
                        disabled={disable}>
                        custom
                    </Button>
                    <Button
                        variant={selectedNode.data?.item?.mode === 'copy' ? 'contained' : undefined}
                        disabled={numberInputs < 1 || disable}
                        onClick={() => handleChange({ ...selectedNode.data.item, mode: 'copy' })}>
                        copy
                    </Button>
                    <Button
                        variant={selectedNode.data?.item?.mode === 'merge' ? 'contained' : undefined}
                        disabled={numberInputs < 2 || disable}
                        onClick={() => handleChange({ ...selectedNode.data.item, mode: 'merge' })}>
                        merge
                    </Button>
                </ButtonGroup>
            </Stack>
            <Stack>
                {(selectedNode.data?.item?.mode === 'custom')
                    ? renderCustom()
                    : selectedNode.data?.item?.mode === 'copy'
                        ? renderCopy()
                        : selectedNode.data?.item?.mode === 'merge'
                            ? renderMerge()
                            : null}
            </Stack>
        </Stack>
    )
}

export default NodeVariableEditor
